import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { v2SettingsApi, type CatalogEntry } from './v2-settings-api';

export const SystemDefaultsEditor: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<Record<string, CatalogEntry>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [catalogRes, effectiveRes] = await Promise.all([
        v2SettingsApi.getCatalog(),
        v2SettingsApi.getEffective({}),
      ]);
      const settings = catalogRes?.settings ?? {};
      const resolved = effectiveRes?.resolved ?? {};
      setCatalog(settings);
      const d: Record<string, string> = {};
      for (const name of Object.keys(settings)) {
        const def = settings[name];
        if (!def.allowedLevels.includes('system')) continue;
        const current = resolved[name]?.value ?? def.default;
        if (current !== undefined) d[name] = JSON.stringify(current);
      }
      setDrafts(d);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadAll();
      } finally {
        if (cancelled) {
          // no-op — guard preserved for unmount safety
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (name: string) => {
    setError(null);
    setInfo(null);
    setSaving(name);
    try {
      const raw = drafts[name];
      let parsed: unknown;
      try { parsed = JSON.parse(raw); }
      catch { throw new Error('Value must be valid JSON (use quotes for strings)'); }

      await v2SettingsApi.patchSystemDefault(name, parsed);
      setInfo(`Saved ${name}`);
      // Refetch so the editor reflects the persisted value (and any
      // server-side coercion / normalization).
      await loadAll();
    } catch (err: any) {
      setError(`Failed to save ${name}: ${err?.response?.data?.message || err.message}`);
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  const entries = Object.entries(catalog)
    .filter(([, def]) => def.allowedLevels.includes('system'))
    .sort(([a], [b]) => a.localeCompare(b));

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }}>{info}</Alert>}
      <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'warning.main' }}>
        Changes write directly to SystemSettings for the current environment. Values shown are the currently persisted system defaults (falling back to catalog defaults if unset).
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Setting</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Value (JSON)</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map(([name, def]) => (
            <TableRow key={name} hover>
              <TableCell sx={{ fontFamily: 'monospace', verticalAlign: 'top' }}>{name}</TableCell>
              <TableCell sx={{ verticalAlign: 'top' }}>{def.type}</TableCell>
              <TableCell>
                <TextField
                  size="small"
                  fullWidth
                  value={drafts[name] ?? ''}
                  onChange={(e) => setDrafts({ ...drafts, [name]: e.target.value })}
                  multiline={def.type === 'object' || def.type === 'stringArray'}
                />
              </TableCell>
              <TableCell sx={{ verticalAlign: 'top' }}>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={saving === name}
                    onClick={() => save(name)}
                  >
                    {saving === name ? 'Saving…' : 'Save'}
                  </Button>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
